import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import { useOrganization } from "@/hooks/use-organization";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { ProviderPoolSelect } from "@/components/Selects/provider-pool-select";

interface AddToProviderPoolDialogProps {
  userId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function AddToProviderPoolDialog({
  userId,
  isOpen,
  onOpenChange,
  onSuccess,
}: AddToProviderPoolDialogProps) {
  const trpc = useTRPC();
  const { selectedOrgBusinessUnit } = useOrganization();

  const [selectedPools, setSelectedPools] = useState<
    Array<{ name: string; id: string }>
  >([]);

  const addToPoolsMutation = useMutation(
    trpc.providerPool.addProviderToPools.mutationOptions({
      onSuccess: () => {
        toast.success("Provider added to pools successfully");
        setSelectedPools([]);
        onOpenChange(false);
        onSuccess?.();
      },
      onError: (error) => {
        toast.error("Failed to add provider to pools", {
          description: error.message,
        });
      },
    }),
  );

  const handleAddToPools = () => {
    if (selectedPools.length === 0) {
      toast.error("Please select at least one provider pool");
      return;
    }
    addToPoolsMutation.mutate({
      providerPoolIds: selectedPools.map((pool) => pool.id),
      userId: userId,
      organizationBusinessUnitId: selectedOrgBusinessUnit.id,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add to Provider Pools</DialogTitle>
          <DialogDescription>
            Select the provider pools you want to add this provider to.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <ProviderPoolSelect
            isMulti={true}
            value={selectedPools}
            onChange={setSelectedPools}
            placeholder="Select provider pools"
          />
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={addToPoolsMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddToPools}
            disabled={
              addToPoolsMutation.isPending || selectedPools.length === 0
            }
          >
            {addToPoolsMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Adding...
              </>
            ) : (
              "Add to Pools"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
